import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Receipt, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";
import { StatusMarker } from "@/components/portal/StatusMarker";
import { ExportBar } from "@/components/portal/ExportBar";
import { BillingRunner } from "@/components/control/BillingRunner";

const PORTAL_EXPORTS = ["providers", "customers", "inquiries", "packages", "subscriptions", "invoices", "documents"];

const empty = { provider_id: "", package_id: null, amount: 0, currency: "CHF", status: "draft", payment_method: "invoice", due_date: "", external_invoice_id: "", notes: "" };
const STATUSES = ["draft", "open", "paid", "overdue", "cancelled"];
const STATUS_STYLE = {
  draft: { bg: "var(--surface-alt)", fg: "var(--text-muted)" },
  open: { bg: "#DBEAFE", fg: "#1D4ED8" },
  paid: { bg: "#DCFCE7", fg: "#15803D" },
  overdue: { bg: "#FEE2E2", fg: "#B91C1C" },
  cancelled: { bg: "var(--surface-alt)", fg: "var(--text-muted)" },
};

export function InvoicesManager() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [providers, setProviders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/invoices");
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/admin/providers").then(({ data }) => setProviders(data)).catch(() => setProviders([]));
  }, []);

  const openNew = () => { setForm({ ...empty, provider_id: providers[0]?.id || "" }); setEditing("new"); };
  const openEdit = (i) => { setForm({ ...empty, ...i }); setEditing(i.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/invoices", form);
      else await api.put(`/admin/invoices/${editing}`, form);
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const markPaid = async (i) => {
    await api.put(`/admin/invoices/${i.id}`, { ...i, status: "paid" });
    load();
  };
  const remove = async (id) => { await api.delete(`/admin/invoices/${id}`); load(); };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!items) return <Loader />;

  return (
    <div className="space-y-4" data-testid="invoices-manager">
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg" style={{ background: "var(--surface-alt)", border: "1px dashed var(--border)" }}>
        <StatusMarker kind="database_only" testid="invoices-payment-marker" />
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>{t("marker.payments.note")}</span>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <BillingRunner scope="portal" onDone={load} />
        <ExportBar basePath="/admin/export" entities={PORTAL_EXPORTS} />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{items.length} · {t("dash.invoices")}</p>
        <Btn onClick={openNew} data-testid="inv-new-btn"><Plus className="h-4 w-4" /> {t("inv.new")}</Btn>
      </div>

      {items.length === 0 && (
        <Card className="p-12 flex flex-col items-center gap-3 text-center" data-testid="inv-empty">
          <Receipt className="h-8 w-8" style={{ color: "var(--accent)" }} />
          <span style={{ color: "var(--text-muted)" }}>{t("inv.none")}</span>
        </Card>
      )}

      <div className="space-y-2">
        {items.map((i) => {
          const s = STATUS_STYLE[i.status] || STATUS_STYLE.draft;
          return (
            <Card key={i.id} className="p-4 flex flex-wrap items-center gap-3 kw-fade-up" data-testid={`inv-item-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" style={{ color: "var(--text)" }}>{i.provider_name || "—"}</div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("inv.due")}: {i.due_date || "—"}</div>
              </div>
              <div className="font-heading text-lg font-bold" style={{ color: "var(--text)" }}>{i.currency} {i.amount}</div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: s.bg, color: s.fg }} data-testid={`inv-status-${i.id}`}>{t(`inv.${i.status}`)}</span>
              <div className="flex items-center gap-2 ml-auto">
                {i.status !== "paid" && <Btn variant="ghost" onClick={() => markPaid(i)} className="h-9 px-3 text-xs" data-testid={`inv-markpaid-${i.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {t("inv.markPaid")}</Btn>}
                <Btn variant="soft" onClick={() => openEdit(i)} className="h-9 px-3 text-xs" data-testid={`inv-edit-${i.id}`}><Pencil className="h-3.5 w-3.5" /></Btn>
                <Btn variant="danger" onClick={() => remove(i.id)} className="h-9 px-3" data-testid={`inv-delete-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn>
              </div>
            </Card>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="inv-modal">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Receipt className="h-5 w-5" style={{ color: "var(--accent)" }} /> {editing === "new" ? t("inv.new") : t("promo.edit")}</h3>
              <button onClick={close} data-testid="inv-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("inv.provider")}</label>
                <select data-testid="inv-provider-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                  value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>
                  <option value="">—</option>
                  {providers.map((p) => <option key={p.id} value={p.id}>{p.business_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("inv.amount")}</label>
                  <input data-testid="inv-amount-input" type="number" min="0" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("control.status")}</label>
                  <select data-testid="inv-status-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`inv.${s}`)}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("inv.due")}</label>
                  <input data-testid="inv-due-input" type="date" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("inv.externalId")}</label>
                  <input data-testid="inv-extid-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.external_invoice_id || ""} onChange={(e) => setForm({ ...form, external_invoice_id: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("inv.notes")}</label>
                <textarea data-testid="inv-notes-input" rows={2} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st}
                  value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <Btn onClick={save} disabled={saving || !form.provider_id} className="w-full" data-testid="inv-save-btn">{t("common.save")}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
